import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, MapPin, Users } from "lucide-react";
import { api, wpImg } from "@/lib/api";
import PrayerTimesWidget from "@/components/PrayerTimesWidget";
import HeroSlider from "@/components/HeroSlider";
import QuranCard from "@/components/QuranCard";
import NextPrayerBadge from "@/components/NextPrayerBadge";
import AsmaUlHusnaTicker from "@/components/AsmaUlHusnaTicker";
import ImpactCounters from "@/components/ImpactCounters";
import BismillahShowcase from "@/components/BismillahShowcase";

export default function Home() {
  const [events, setEvents] = useState([]);
  const [services, setServices] = useState([]);
  const [imams, setImams] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/imams").then((r) => setImams(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <HeroSlider>
        <div className="mb-6">
          <NextPrayerBadge />
        </div>
        <p className="mt-2 text-lg md:text-xl text-cream/85 font-light max-w-xl leading-relaxed">
          At Flint Islamic Center, we are dedicated to upholding the teachings of Islam
          exemplified by the Prophet Muhammad ﷺ — serving, learning, and building together.
        </p>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/donate"
            data-testid="hero-donate-btn"
            className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-7 py-3.5 text-sm font-semibold uppercase tracking-wider hover:bg-gold-600 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-gold/30"
          >
            <Heart size={14} /> Support FIC
          </Link>
          <Link
            to="/about"
            data-testid="hero-about-btn"
            className="inline-flex items-center gap-2 rounded-full border border-cream/30 text-cream px-7 py-3.5 text-sm font-medium uppercase tracking-wider hover:bg-cream/10 transition-colors"
          >
            Our story <ArrowRight size={14} />
          </Link>
        </div>
      </HeroSlider>

      {/* Prayer times band - directly under hero */}
      <section className="px-5 md:px-10 mt-10 md:mt-12 relative z-10 pb-6">
        <div className="max-w-6xl mx-auto">
          <PrayerTimesWidget compact />
        </div>
      </section>

      {/* EVENTS — moved up to sit directly under Prayer times */}
      {events.length > 0 && (
        <section className="relative py-20 md:py-24 px-6 md:px-10 overflow-hidden">
          {/* Subtle Islamic-pattern overlay so this section has its own visual identity */}
          <div className="absolute inset-0 pattern-girih opacity-[0.04] pointer-events-none" />
          <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-gold/8 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto">
            <div className="flex flex-wrap justify-between items-end gap-6 mb-12">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Mark your calendar</div>
                <h2 className="font-serif text-4xl md:text-5xl mt-3 text-ink">Upcoming events</h2>
              </div>
              <Link to="/events" className="text-sm font-medium text-navy inline-flex items-center gap-2 hover:gap-3 transition-all">
                All events <ArrowRight size={14} />
              </Link>
            </div>
            {/* Center one event; 2 side-by-side when both exist; 3 columns
                from 3+ events. The slice cap of 3 keeps the homepage tight —
                visitors can hit "All events →" to see the rest. */}
            <div className={
              events.length === 1
                ? "max-w-2xl mx-auto"
                : events.length === 2
                ? "grid md:grid-cols-2 gap-6"
                : "grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            }>
              {events.slice(0, 3).map((e) => <EventCardFeatured key={e.id} event={e} />)}
            </div>
          </div>
        </section>
      )}

      {/* ASMA UL HUSNA — gentle marquee of the 99 Names. Breaks up the
          long stretch of cream/sand sections with a deep emerald band. */}
      <AsmaUlHusnaTicker />

      {/* WELCOME */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 bg-gradient-to-b from-cream via-white to-cream/40 overflow-hidden">
        {/* Floating ornate orbs — soft, slow, living. They give the section
            its own atmosphere without competing with the content. */}
        <div className="absolute top-20 left-[8%] w-2 h-2 rounded-full bg-gold/30 home-float" />
        <div className="absolute top-40 left-[18%] w-1 h-1 rounded-full bg-gold/40 home-float-slow" />
        <div className="absolute bottom-32 right-[12%] w-3 h-3 rounded-full bg-navy/15 home-float-delayed" />
        <div className="absolute top-1/3 right-[6%] w-1.5 h-1.5 rounded-full bg-gold/35 home-float" />
        {/* Subtle radial gold glow on the right side */}
        <div className="absolute -bottom-32 -right-20 w-[500px] h-[500px] rounded-full bg-gold/[0.06] blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Welcome</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-ink">
              More than a masjid —<br />a gathering of <em className="text-navy">hearts</em>.
            </h2>
            <p className="mt-6 text-ink/70 leading-relaxed text-lg font-light">
              Whether you are seeking spiritual guidance, community events, or educational resources,
              you'll find a wealth of information and support here. Explore our programs, join our
              events, and help us build a stronger, more harmonious community together.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 text-navy font-medium hover:gap-3 transition-all"
              data-testid="welcome-read-more"
            >
              Read our full story <ArrowRight size={16} />
            </Link>
          </div>
          <BismillahShowcase />
        </div>
      </section>

      {/* QURAN CARD */}
      <QuranCard />

      {/* IMPACT COUNTERS — animated by-the-numbers strip, dark navy band */}
      <ImpactCounters />

      {/* SERVICES */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-sand/50 via-cream to-sand/30 px-6 md:px-10 overflow-hidden" id="services">
        {/* Soft Islamic-pattern accent floating across the section */}
        <div className="absolute inset-0 pattern-girih opacity-[0.05] pointer-events-none" />
        <div className="absolute -top-24 left-[15%] w-72 h-72 rounded-full bg-navy/[0.04] blur-3xl pointer-events-none" />
        <div className="absolute bottom-20 right-[5%] w-1.5 h-1.5 rounded-full bg-gold/40 home-float" />
        <div className="absolute top-32 left-[8%] w-1 h-1 rounded-full bg-navy/30 home-float-slow" />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-between items-end gap-6 mb-14">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">What we do</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-ink">Our services</h2>
            </div>
            <Link to="/services" className="text-sm font-medium text-navy inline-flex items-center gap-2 hover:gap-3 transition-all">
              All services <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((s) => (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group relative rounded-3xl overflow-hidden bg-white border border-sand hover:border-navy/30 transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/5 p-7"
              >
                {s.image_url && (
                  <div className="w-16 h-16 rounded-2xl bg-navy/5 flex items-center justify-center mb-5 group-hover:bg-gold/15 transition-colors">
                    <img src={wpImg(s.image_url)} alt="" className="w-10 h-10 object-contain" />
                  </div>
                )}
                <h3 className="font-serif text-2xl text-ink group-hover:text-navy transition-colors">{s.title}</h3>
                <p className="mt-2.5 text-sm text-ink/65 leading-relaxed">{s.summary}</p>
                <div className="mt-5 flex items-center gap-2 text-navy text-sm font-medium">
                  Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* IMAMS */}
      {imams.length > 0 && (
        <section className="relative py-24 md:py-32 bg-navy text-cream px-6 md:px-10 overflow-hidden">
          {/* Deep night-sky vibe with Islamic geometric pattern */}
          <div className="absolute inset-0 pattern-girih opacity-[0.08] pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.18)_0%,rgba(14,42,71,0)_60%)] pointer-events-none" />
          <div className="absolute top-16 left-[10%] w-1 h-1 rounded-full bg-gold/60 home-float-slow" />
          <div className="absolute bottom-24 right-[15%] w-1.5 h-1.5 rounded-full bg-gold/50 home-float-delayed" />
          <div className="absolute top-1/2 right-[8%] w-2 h-2 rounded-full bg-gold/40 home-float" />
          <div className="relative max-w-6xl mx-auto">
            <div className="text-center max-w-xl mx-auto mb-16">
              <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Scholars & Teachers</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-3 text-cream">Our Imams</h2>
              <p className="mt-4 text-cream/70 font-light leading-relaxed">
                Led by deeply knowledgeable scholars committed to nurturing your faith journey.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-10">
              {imams.map((im) => (
                <div key={im.id} className="group text-center" data-testid={`imam-${im.id}`}>
                  <div className="mx-auto w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden bg-cream/10 mb-6 border-4 border-gold/30 shadow-2xl shadow-navy/40 ring-1 ring-gold/40 transition-transform duration-500 group-hover:scale-105">
                    {im.photo_url ? (
                      <img src={wpImg(im.photo_url)} alt={im.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gold"><Users size={32} /></div>
                    )}
                  </div>
                  <div className="font-serif text-xl text-cream">{im.name}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gold mt-1.5 font-semibold">{im.title}</div>
                  <p className="mt-3 text-sm text-cream/70 leading-relaxed max-w-xs mx-auto">{im.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DONATE CTA */}
      <section className="py-20 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-[2.5rem] overflow-hidden bg-navy text-cream p-12 md:p-20">
            <div className="absolute inset-0 pattern-girih opacity-25" />
            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-gold font-semibold">Your support matters</div>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 leading-tight">
                  Build a house of Allah — <em className="text-gold">your</em> house in Jannah.
                </h2>
                <p className="mt-6 text-cream/80 text-lg font-light leading-relaxed max-w-xl">
                  Every contribution — Zakat, Sadaqah, or general donation — fuels prayer, education,
                  and care for the most vulnerable in our community.
                </p>
                <Link
                  to="/donate"
                  data-testid="cta-donate-btn"
                  className="inline-flex items-center gap-3 mt-10 rounded-full bg-gold text-navy px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-gold-600 transition-all hover:-translate-y-0.5"
                >
                  Donate Now <ArrowRight size={14} />
                </Link>
              </div>
              <div className="hidden lg:flex items-center justify-center">
                <DonateIllustration />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function DonateIllustration() {
  return (
    <svg viewBox="0 0 320 320" className="w-full max-w-[340px] h-auto" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="dome" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#C5A059" />
          <stop offset="1" stopColor="#8F7138" />
        </linearGradient>
      </defs>
      {/* Outer ornament */}
      <circle cx="160" cy="160" r="148" stroke="#C5A059" strokeOpacity="0.35" />
      <circle cx="160" cy="160" r="136" stroke="#C5A059" strokeOpacity="0.2" strokeDasharray="2 6" />
      {/* 8-point star */}
      <path d="M160 22 L184 90 L254 104 L204 152 L220 222 L160 188 L100 222 L116 152 L66 104 L136 90 Z"
            stroke="#C5A059" strokeOpacity="0.25" />
      {/* Mosque silhouette */}
      <g transform="translate(82 120)">
        {/* Minaret left */}
        <rect x="6" y="28" width="14" height="86" fill="#C5A059" fillOpacity="0.9" />
        <path d="M13 28 L6 28 L13 14 L20 28 Z" fill="#C5A059" />
        <circle cx="13" cy="10" r="3" fill="#C5A059" />
        {/* Main dome */}
        <path d="M40 70 Q40 28 78 28 Q116 28 116 70 L116 114 L40 114 Z" fill="url(#dome)" />
        <circle cx="78" cy="22" r="4" fill="#C5A059" />
        <path d="M78 2 L78 18" stroke="#C5A059" strokeWidth="2" />
        {/* Minaret right */}
        <rect x="136" y="28" width="14" height="86" fill="#C5A059" fillOpacity="0.9" />
        <path d="M143 28 L136 28 L143 14 L150 28 Z" fill="#C5A059" />
        <circle cx="143" cy="10" r="3" fill="#C5A059" />
        {/* Arch door */}
        <path d="M70 114 L70 86 Q78 70 86 86 L86 114 Z" fill="#0E2A47" />
      </g>
      {/* Heart */}
      <path d="M160 262 C140 245 120 235 120 220 C120 210 128 202 138 202 C146 202 154 208 160 216 C166 208 174 202 182 202 C192 202 200 210 200 220 C200 235 180 245 160 262 Z"
            fill="#C5A059" />
    </svg>
  );
}

function EventCardFeatured({ event }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const d = new Date(event.start_date);
  const diff = d - new Date();
  const days = Math.max(0, Math.floor(diff / 86400000));
  const hasUrl = !!event.image_url;
  return (
    <Link
      to={`/events/${event.id}`}
      className="group relative rounded-3xl overflow-hidden bg-white border border-sand hover:border-navy/30 transition-all hover:shadow-2xl hover:shadow-navy/5"
      data-testid={`event-featured-${event.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-navy">
        {/* Always-on Islamic backdrop so the image area never feels empty */}
        <div className="absolute inset-0 pattern-girih opacity-[0.16]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.25)_0%,transparent_70%)]" />

        {/* Mosque silhouette — visible until/unless the photo loads */}
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center px-8 z-0">
            <svg viewBox="0 0 220 140" className="w-44 md:w-56 h-auto" fill="none" aria-hidden="true">
              <g stroke="#C5A059" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="20" y1="120" x2="20" y2="50" strokeOpacity="0.85" />
                <path d="M14 50 L20 38 L26 50 Z" strokeOpacity="0.85" fill="#C5A059" fillOpacity="0.18" />
                <circle cx="20" cy="32" r="2.5" strokeOpacity="0.85" />
                <path d="M55 120 L55 80 Q55 50 110 50 Q165 50 165 80 L165 120 Z" strokeOpacity="0.95" fill="#C5A059" fillOpacity="0.10" />
                <circle cx="110" cy="42" r="3.5" strokeOpacity="0.95" fill="#C5A059" fillOpacity="0.4" />
                <line x1="110" y1="20" x2="110" y2="38" strokeOpacity="0.85" />
                <path d="M95 120 L95 100 Q110 86 125 100 L125 120" strokeOpacity="0.85" fill="#0E2A47" />
                <line x1="200" y1="120" x2="200" y2="50" strokeOpacity="0.85" />
                <path d="M194 50 L200 38 L206 50 Z" strokeOpacity="0.85" fill="#C5A059" fillOpacity="0.18" />
                <circle cx="200" cy="32" r="2.5" strokeOpacity="0.85" />
                <line x1="0" y1="120" x2="220" y2="120" strokeOpacity="0.5" />
              </g>
            </svg>
          </div>
        )}

        {hasUrl && (
          <>
            <img
              src={wpImg(event.image_url)}
              alt=""
              aria-hidden="true"
              className={`absolute inset-0 w-full h-full object-cover scale-110 blur-2xl transition-opacity duration-500 ${imgLoaded ? "opacity-60" : "opacity-0"}`}
              onLoad={() => setImgLoaded(true)}
            />
            <div className={`absolute inset-0 bg-gradient-to-t from-navy/40 via-transparent to-transparent transition-opacity ${imgLoaded ? "opacity-100" : "opacity-0"}`} />
            <img
              src={wpImg(event.image_url)}
              alt={event.title}
              className={`relative w-full h-full object-contain group-hover:scale-105 transition-all duration-700 drop-shadow-2xl ${imgLoaded ? "opacity-100" : "opacity-0"}`}
              onLoad={() => setImgLoaded(true)}
            />
          </>
        )}

        {/* Date pill — works whether image loads or not */}
        <div className="absolute top-4 left-4 rounded-2xl bg-cream/95 backdrop-blur px-3.5 py-2 text-center shadow-lg z-10">
          <div className="text-[10px] uppercase tracking-wider text-gold font-bold leading-none">
            {d.toLocaleDateString("en-US", { month: "short", timeZone: "America/New_York" })}
          </div>
          <div className="font-serif text-2xl text-navy leading-none mt-1">
            {d.toLocaleDateString("en-US", { day: "numeric", timeZone: "America/New_York" })}
          </div>
        </div>
        {diff > 0 && (
          <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-gold text-navy px-3 py-1 text-[10px] font-bold uppercase tracking-wider shadow-lg z-10">
            <span className="pulse-dot" /> In {days} day{days !== 1 ? "s" : ""}
          </div>
        )}
      </div>
      <div className="p-7 md:p-8">
        {event.location && (
          <div className="flex items-center gap-1.5 text-xs text-ink/55 uppercase tracking-wider">
            <MapPin size={12} className="text-gold" /> {event.location.split(",")[0]}
          </div>
        )}
        <h3 className="font-serif text-2xl md:text-3xl mt-2 text-ink group-hover:text-navy transition-colors">{event.title}</h3>
        <p className="mt-3 text-sm text-ink/65 leading-relaxed line-clamp-3">{event.description}</p>
        <div className="mt-5 inline-flex items-center gap-2 text-navy text-sm font-medium">
          View details <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
}
